import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Api
import client from '@api';

// GraphQL
import { unreadNotificationsCountQuery } from '@graphql/queries/notifications';
import { onNotificationSubscription } from '@graphql/subscription/notifications';

// Hooks
import { useWsReconnect } from '@hooks/useWsReconnect';

// Services
import { ErrorCategory, handleError } from '@services/errorService';

interface UseUnreadNotificationsCountReturn {
  unreadCount: number;
  refetch: () => void;
  resetCount: () => void;
}

const useUnreadNotificationsCount = (
  enabled = true,
): UseUnreadNotificationsCountReturn => {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const mountedRef = useRef(true);
  const { pathname } = useLocation();
  const isOnNotificationsPage = pathname.includes('/notifications');

  const fetchCount = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await client().query({
        query: unreadNotificationsCountQuery,
        fetchPolicy: 'no-cache',
      });
      if (mountedRef.current) {
        setUnreadCount(data?.unreadNotificationsCount ?? 0);
      }
    } catch (err) {
      handleError(err, { category: ErrorCategory.API, silent: true });
    }
  }, [enabled]);

  const resetCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Reset count when entering notifications page
  useEffect(() => {
    if (isOnNotificationsPage) {
      setUnreadCount(0);
    }
  }, [isOnNotificationsPage]);

  // Fetch initial count
  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Per ADR-009 — re-fetch the unread count on every WS reconnect so we don't
  // silently miss notifications that arrived during the offline window.
  useWsReconnect(fetchCount);

  // Subscribe to new notifications — only increment when not on notifications page
  useEffect(() => {
    if (!enabled) return;

    const observable = client().subscribe({
      query: onNotificationSubscription,
    });

    const subscription = observable.subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: ({ data }: { data: any }) => {
        if (data?.onNotification && mountedRef.current) {
          setUnreadCount((prev) => prev + 1);
        }
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [enabled]);

  // Cleanup
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { unreadCount, refetch: fetchCount, resetCount };
};

export default useUnreadNotificationsCount;
